import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import './CourierRequestPage.css';

const CourierLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await api.post('/auth/login', { email, password }, { 
        withCredentials: true,
        headers: { "Content-Type": "application/json" }
      });

      if (response.data?.success) {
        const { vendorId } = response.data;
        
        // Fetch vendor profile to check courier service status
        const profileResponse = await api.get('/vendors/profile', {
          withCredentials: true
        });
        
        if (profileResponse.data?.courierService?.enabled) {
          await login(vendorId);
          navigate('/courier-dashboard');
        } else {
          setError('This account does not have courier service enabled. Please contact support.');
          setLoading(false);
          return;
        }
      } else {
        throw new Error(response.data?.message || 'Unexpected response structure');
      }
    } catch (err) {
      console.error('Login error:', err);
      setError(
        err.response?.data?.message || 
        'Login failed. Please check your credentials.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-container">
      {/* Header */}
      <header className="header">
        <div className="header-container">
          <div className="logo-container">
            <a href="/courier">
              <img 
                src={require('../assets/images/courierLogo.png')}
                alt="Weedhurry Logo"
              />
            </a>
            <h1 className="site-title"></h1>
          </div>
          <nav>
            <ul className="nav-menu">
              <li className="nav-item">
                <a href="/courier-request" className="nav-link">Request Courier</a>
              </li>
            </ul>
          </nav>
        </div>
      </header>

      {/* Main Content */}
      <main className="main-content">
        <div className="content-container">
          <div className="page-section">
            <div style={{ maxWidth: '420px', margin: '0 auto' }}>
              <h2 className="page-title" style={{ textAlign: 'center' }}>
                Courier Partner Login
              </h2>
              <p className="page-description" style={{ textAlign: 'center', marginBottom: '2rem' }}>
                Sign in to your courier service dashboard
              </p>
              
              {error && (
                <div style={{
                  backgroundColor: '#fee2e2',
                  border: '1px solid #fecaca',
                  borderRadius: '0.5rem',
                  padding: '1rem',
                  marginBottom: '1.5rem',
                  color: '#991b1b'
                }}>
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit}>
                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{ 
                    display: 'block',
                    color: '#4b5563',
                    fontWeight: '600',
                    marginBottom: '0.5rem'
                  }}>
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '0.5rem',
                      backgroundColor: '#ffffff'
                    }}
                    placeholder="Enter your email"
                  />
                </div>

                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{ 
                    display: 'block',
                    color: '#4b5563',
                    fontWeight: '600',
                    marginBottom: '0.5rem'
                  }}>
                    Password
                  </label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '0.5rem',
                      backgroundColor: '#ffffff'
                    }}
                    placeholder="Enter your password"
                  />
                </div>

                <button
                  type="submit"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    backgroundColor: '#166534',
                    color: 'white',
                    border: 'none',
                    borderRadius: '0.5rem',
                    fontWeight: '600',
                    marginBottom: '1rem',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    opacity: loading ? 0.7 : 1
                  }}
                  disabled={loading}
                >
                  {loading ? 'Signing In...' : 'Sign In'}
                </button>

                <div style={{ textAlign: 'center' }}>
                  <Link 
                    to="/reset-password-request" 
                    style={{ 
                      color: '#4b5563',
                      textDecoration: 'none',
                      fontSize: '0.95rem'
                    }}
                  >
                    Forgot your password?
                  </Link>
                </div>
                
                <div style={{ textAlign: 'center', marginTop: '1rem' }}>
                  <p style={{ color: '#4b5563' }}>
                    Not a courier partner? {' '}
                    <Link 
                      to="/courier"
                      style={{ 
                        color: '#166534',
                        fontWeight: '600',
                        textDecoration: 'none'
                      }}
                    >
                      Register here
                    </Link>
                  </p>
                </div>
              </form>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-container">
          <div className="footer-grid">
            <div className="footer-section">
              <h3 className="footer-title">Weedhurry</h3>
              <p className="footer-text">
                Your trusted cannabis delivery platform connecting dispensaries with reliable couriers.
              </p>
            </div>
            <div className="footer-section">
              <h3 className="footer-title">Quick Links</h3>
              <ul className="footer-menu">
                <li className="footer-menu-item">
                  <a href="/" className="footer-link">Home</a>
                </li>
                <li className="footer-menu-item">
                  <a href="/courier" className="footer-link">Services</a>
                </li>
                <li className="footer-menu-item">
                  <a href="/contact" className="footer-link">Contact</a>
                </li>
              </ul>
            </div>
            <div className="footer-section">
              <h3 className="footer-title">Resources</h3>
              <ul className="footer-menu">
                <li className="footer-menu-item">
                  <a href="/courier#wh-faq" className="footer-link">FAQ</a>
                </li>
                <li className="footer-menu-item">
                  <a href="/terms" className="footer-link">Terms of Service</a>
                </li>
                <li className="footer-menu-item">
                  <a href="/terms" className="footer-link">Privacy Policy</a>
                </li>
              </ul>
            </div>
            <div className="footer-section">
              <h3 className="footer-title">Connect With Us</h3>
              <p className="contact-info">
                Contact Us: <a href="mailto:info@weedhurry.com" className="footer-link underline">info@weedhurry.com</a>
              </p>
            </div>
          </div>
          <div className="footer-divider">
            <p>© {new Date().getFullYear()} WeedHurry. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default CourierLogin;